import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { NotificationComponent } from '../notification/notification.component';
import { AuthorizationService, PERMISSION_CONFIG, TYPE, FeUtil, BeUtil, TabManagerService } from '../../../hezky-routes';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('notification') notification!: NotificationComponent;
  drawer: boolean = false;
  isNotification: boolean = false;
  locationVisible: boolean = false;
  productVisible: boolean = false;
  routeData: TYPE.MenuItem[] = [];
  selectedApplication!: number;
  selectedApplicationName!: string;
  private displayProductSub?: Subscription;

  storageRoutes: TYPE.MenuItem[] = [
    {
      label: 'Dashboard',
      type: 'link',
      routerLink: '/dashboard/storage',
      permission: PERMISSION_CONFIG.Module['dashboard']
    },
    {
      label: 'Assets',
      type: 'link',
      routerLink: '/assets',
      permission: PERMISSION_CONFIG.Module['assets']
    },
    {
      label: 'Incidents',
      type: 'link',
      routerLink: '/storage-incidents/list',
      permission: PERMISSION_CONFIG.Module['storageIncidents']
    },
    {
      label: 'Reports',
      type: 'link',
      routerLink: '/reports',
      permission: PERMISSION_CONFIG.Module['reports']
    },
    {
      label: 'Admin',
      type: 'submenu',
      permission: PERMISSION_CONFIG.Module['admin'],
      children: [
        {
          label: 'Location Management',
          type: 'link',
          routerLink: '/location-management',
          permission: PERMISSION_CONFIG.Module['locationManagement']
        },
        {
          label: 'Product Management',
          type: 'link',
          routerLink: '/product-management',
          permission: PERMISSION_CONFIG.Module['productManagement']
        },
        {
          label: 'User Management',
          type: 'link',
          routerLink: '/user-management',
          permission: PERMISSION_CONFIG.Module['userManagement']
        },
        {
          label: 'Device Management',
          type: 'link',
          routerLink: '/device-management',
          queryParams: { tab: 'gateway-management' },
          permission: PERMISSION_CONFIG.Module['deviceManagement']
        }
      ]
    }
  ];

  transportRoutes: TYPE.MenuItem[] = [
    {
      label: 'Dashboard',
      type: 'link',
      routerLink: '/dashboard/transport',
      permission: PERMISSION_CONFIG.Module['dashboard']
    },
    {
      label: 'Trips',
      type: 'link',
      routerLink: '/trips',
      permission: PERMISSION_CONFIG.Module['trips']
    },
    {
      label: 'Incidents',
      type: 'link',
      routerLink: '/transport-incidents/list',
      permission: PERMISSION_CONFIG.Module['transportIncidents']
    },
    {
      label: 'Inventory Management',
      type: 'link',
      routerLink: '/inventory-management',
      queryParams: { tab: 'all' },
      permission: PERMISSION_CONFIG.Module['inventoryManagement']
    },
    {
      label: 'Reports',
      type: 'link',
      routerLink: '/reports',
      permission: PERMISSION_CONFIG.Module['reports']
    },
    {
      label: 'Admin',
      type: 'submenu',
      permission: PERMISSION_CONFIG.Module['admin'],
      children: [
        {
          label: 'Location Management',
          type: 'link',
          routerLink: '/location-management',
          permission: PERMISSION_CONFIG.Module['locationManagement']
          // children: [
          //   {
          //     label: 'Internal Location Management',
          //     type: 'link',
          //     routerLink: '/location-management',
          //     permission: PERMISSION_CONFIG.Module['locationManagement']
          //   },
          //   {
          //     label: 'External Location Management',
          //     type: 'link',
          //     routerLink: '/quick-location-management',
          //     permission: PERMISSION_CONFIG.Module['quickLocationManagement']
          //   }
          // ]
        },
        {
          label: 'Alarm Management',
          type: 'link',
          routerLink: '/alarm-management',
          permission: PERMISSION_CONFIG.Module['alarmManagement']
        },
        {
          label: 'Logistic Management',
          type: 'link',
          routerLink: '/logistic-management',
          permission: PERMISSION_CONFIG.Module['logisticManagement']
        },
        {
          label: 'Product Management',
          type: 'link',
          routerLink: '/product-management',
          permission: PERMISSION_CONFIG.Module['productManagement']
        },
        {
          label: 'User Management',
          type: 'link',
          routerLink: '/user-management',
          permission: PERMISSION_CONFIG.Module['userManagement']
        }
      ]
    }
  ];

  aqiRoutes: TYPE.MenuItem[] = [
    {
      label: 'Dashboard',
      type: 'link',
      routerLink: '/dashboard/aqi',
    }
  ];

  constructor(
    protected readonly authorizationSvc: AuthorizationService,
    protected feUtil: FeUtil,
    protected beUtil: BeUtil,
    private tabManager: TabManagerService
  ) { }

  ngOnInit(): void {
    this.setSelectedApplication();
    this.initStoreData();
    this.displayProductSub = this.feUtil.getDisplayProduct.pipe(distinctUntilChanged()).subscribe((product) => {
      if (product) {
        this.selectedApplication = product.id;
        this.selectedApplicationName = product.name;
        this.setRouteData(this.selectedApplicationName.toLowerCase());
        if (this.selectedApplicationName.toLowerCase() === 'transport') {
          this.beUtil.customerSvc.getSenderLocations();
        }
      }
    });
  }

  private initStoreData(): void {
    this.beUtil.customerSvc.getLocationTree();
    this.beUtil.notificationSvc.updateNotificationCount();
    this.beUtil.customerSvc.customerConfig();
    this.beUtil.customerSvc.changeEnvironment();
    this.beUtil.customerSvc.getAbbreviations();
  }

  ngOnDestroy(): void {
    this.displayProductSub?.unsubscribe();
  }

  private setSelectedApplication(): void {
    this.selectedApplicationName = this.feUtil.selectedApp;
    this.selectedApplication = this.feUtil.appId;
    this.setRouteData(this.selectedApplicationName);
  }

  handleProductClick(productId: number): void {
    const app = this.feUtil.applications.find((a: TYPE.Application) => a.id === productId);
    if (!app) return;
    const appName = app.name || '';
    const appNameLower = appName.toLowerCase();

    const broadcastChannel = new BroadcastChannel('tab_management');
    try {
      broadcastChannel.postMessage({
        type: 'application_changed',
        application_name: appNameLower,
        application_id: productId,
        sourceTabId: this.tabManager.getTabId()
      });
    } finally {
      broadcastChannel.close();
    }

    this.feUtil.switchApplication(productId);
    this.selectedApplication = productId;
    this.selectedApplicationName = appNameLower;
    this.feUtil.router.navigate([`dashboard/${appNameLower}`]);
    this.setRouteData(appName);
  }

  private setRouteData(product: string | null) {
    if (product == 'storage') {
      this.routeData = this.storageRoutes;
    } else if (product == 'transport') {
      this.routeData = this.transportRoutes;
    } else if (product == 'aqi') {
      this.routeData = this.aqiRoutes;
    } else {
      this.routeData = [];
    }
  }

  protected getApplicationConfig(applicationName: string): { icon: string; title: string; description: string, active: boolean, iconSize: string } {
    const accessRight = this.feUtil.user_data?.userRoles?.privilege;
    const configs: Record<string, { icon: string; title: string; description: string, active: boolean, iconSize: string }> = {
      corporate: {
        icon: 'dashboard',
        title: 'Comprehensive Dashboard',
        description: 'Corporate dashboard and statistics',
        iconSize: 'lg',
        active: true
      },
      storage: {
        icon: 'hezky:Storage',
        title: 'Storage',
        description: 'Monitor all storage units',
        iconSize: 'lg',
        active: true
      },
      transport: {
        icon: 'truck',
        title: 'Transport',
        description: 'Track all transport status',
        iconSize: 'lg',
        active: PERMISSION_CONFIG.Module['transportDashboard'].accessRight?.includes(accessRight) ? true : false
      },
      aqi: {
        icon: 'hezky:Wind',
        title: 'AQI',
        description: 'Monitor air quality data',
        iconSize: 'xl',
        active: true
      },
      manufacture: {
        icon: 'hezky:Manufacture',
        title: 'Manufacturing',
        description: 'Production status coming soon',
        iconSize: 'lg',
        active: false
      }
    };
    return configs[applicationName] || { icon: '', title: '', description: '' };
  }
}
